import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { PiCheckCircleBold, PiPencilSimpleBold, PiPlusBold, PiXCircleBold } from 'react-icons/pi';
import { Button, LoadingState } from '@v2/design-system';
import RichListShell, { BadgePill, type Column } from '@v2/components/RichListShell';
import { useResourceList, useResourceMutation, useResourceShow } from '@v2/hooks/useResourceCrud';

import type { UseFormRegister, UseFormWatch } from 'react-hook-form';

/** Swatch circular de cor com tooltip do valor hex */
function ColorSwatch({ hex }: { hex?: string | null }) {
    if (!hex) {
        return (
            <span
                className="inline-block size-8 rounded-full border-2 border-dashed border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-800"
                title="Sem cor"
            />
        );
    }
    return (
        <span className="flex items-center gap-2">
            <span
                className="inline-block size-8 rounded-full border-2 border-white shadow-md ring-1 ring-gray-200 dark:ring-gray-700"
                style={{ backgroundColor: hex }}
                title={hex}
            />
            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{hex}</span>
        </span>
    );
}

type FieldDef = { key: string; type: string; label: string; required?: boolean; options?: Record<string, string> };

type ResourceConfig = {
    api: string;
    label: string;
    singular: string;
    fields: FieldDef[];
    columns: Array<{ key: string; label: string; type?: string }>;
};

/** Campo de cor com preview visual + picker nativo */
function ColorPickerField({
    fieldKey,
    register,
    watch,
}: {
    fieldKey: string;
    register: UseFormRegister<Record<string, unknown>>;
    watch: UseFormWatch<Record<string, unknown>>;
}) {
    const current = (watch(fieldKey) as string | undefined) ?? '';
    const { ref, onChange, ...restReg } = register(fieldKey);

    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="relative shrink-0">
                <span
                    className="flex size-16 items-center justify-center rounded-2xl shadow-md ring-2 ring-white dark:ring-gray-800"
                    style={{ backgroundColor: current || '#e5e7eb' }}
                >
                    <input
                        type="color"
                        value={current || '#aaaaaa'}
                        onChange={(e) => {
                            const synth = { target: { name: restReg.name, value: e.target.value } } as React.ChangeEvent<HTMLInputElement>;
                            void onChange(synth);
                        }}
                        className="absolute inset-0 h-full w-full cursor-pointer rounded-2xl opacity-0"
                        title="Escolher cor"
                    />
                </span>
            </div>
            <div className="flex flex-1 flex-col gap-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Valor hex</p>
                <input
                    type="text"
                    ref={ref}
                    onChange={onChange}
                    {...restReg}
                    placeholder="#RRGGBB"
                    className="w-44 rounded-xl border border-gray-200 px-3 py-2 font-mono text-sm outline-none transition focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-400">Clique na amostra ou digite o hex</p>
            </div>
            {current && (
                <div className="flex flex-col items-center gap-1">
                    <span
                        className="size-10 rounded-full border-4 border-white shadow-lg ring-1 ring-gray-200 dark:border-gray-800 dark:ring-gray-700"
                        style={{ backgroundColor: current }}
                    />
                    <span className="font-mono text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400">
                        {current}
                    </span>
                </div>
            )}
        </div>
    );
}

const SUPPLIER_FALLBACK = '/img/favicon.png';

const RESOURCE_MAP: Record<string, ResourceConfig> = {
    vessels: {
        api: 'vessels',
        label: 'Navios',
        singular: 'Navio',
        fields: [
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'imo_number', type: 'text', label: 'IMO' },
            { key: 'shipping_line', type: 'text', label: 'Armador' },
            { key: 'flag', type: 'text', label: 'Bandeira' },
            { key: 'sort_order', type: 'number', label: 'Ordem' },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'name', label: 'Nome' },
            { key: 'imo_number', label: 'IMO' },
            { key: 'active', label: 'Ativo', type: 'boolean' },
        ],
    },
    sectors: {
        api: 'sectors',
        label: 'Setores',
        singular: 'Setor',
        fields: [
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'code', type: 'text', label: 'Código', required: true },
            { key: 'color', type: 'text', label: 'Cor', required: true },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'name', label: 'Nome' },
            { key: 'code', label: 'Código' },
        ],
    },
    suppliers: {
        api: 'suppliers',
        label: 'Fornecedores',
        singular: 'Fornecedor',
        fields: [
            { key: 'slug', type: 'text', label: 'Slug', required: true },
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'logo_url', label: '', type: 'logo' },
            { key: 'name', label: 'Nome' },
            { key: 'slug', label: 'Slug' },
            { key: 'product_supplier_code', label: 'Código produto' },
            { key: 'active', label: 'Ativo', type: 'boolean' },
        ],
    },
    'company-branches': {
        api: 'company-branches',
        label: 'Filiais Buckler',
        singular: 'Filial',
        fields: [
            { key: 'slug', type: 'text', label: 'Slug', required: true },
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'cnpj', type: 'text', label: 'CNPJ' },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'name', label: 'Nome' },
            { key: 'cnpj', label: 'CNPJ' },
        ],
    },
    'customs-brokers': {
        api: 'customs-brokers',
        label: 'Despachantes',
        singular: 'Despachante',
        fields: [
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'email', type: 'text', label: 'E-mail' },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [{ key: 'name', label: 'Nome' }],
    },
    'freight-agents': {
        api: 'freight-agents',
        label: 'Agentes de Carga',
        singular: 'Agente',
        fields: [
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'email', type: 'text', label: 'E-mail' },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [{ key: 'name', label: 'Nome' }],
    },
    'customs-channels': {
        api: 'customs-channels',
        label: 'Canais Aduaneiros',
        singular: 'Canal',
        fields: [
            { key: 'code', type: 'text', label: 'Código', required: true },
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'code', label: 'Código' },
            { key: 'name', label: 'Nome' },
        ],
    },
    'task-templates': {
        api: 'task-templates',
        label: 'Templates de Tarefas',
        singular: 'Template',
        fields: [
            { key: 'stage', type: 'text', label: 'Estágio', required: true },
            { key: 'title', type: 'text', label: 'Título', required: true },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'stage', label: 'Estágio' },
            { key: 'title', label: 'Título' },
        ],
    },
    'product-colors': {
        api: 'product-colors',
        label: 'Cores de Produto',
        singular: 'Cor',
        fields: [
            { key: 'category', type: 'text', label: 'Categoria', required: true },
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'hex', type: 'color', label: 'Cor (hex)' },
            { key: 'sort_order', type: 'number', label: 'Ordem' },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'hex', label: '', type: 'color' },
            { key: 'name', label: 'Nome' },
            { key: 'category', label: 'Categoria' },
            { key: 'active', label: 'Ativo', type: 'boolean' },
        ],
    },
    'system-integrations': {
        api: 'system-integrations',
        label: 'Integrações',
        singular: 'Integração',
        fields: [
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'type', type: 'text', label: 'Tipo', required: true },
            { key: 'is_active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'name', label: 'Nome' },
            { key: 'type', label: 'Tipo' },
        ],
    },
    'distribution-centers': {
        api: 'distribution-centers',
        label: 'Centros de distribuição',
        singular: 'Centro de distribuição',
        fields: [
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'slug', type: 'text', label: 'Slug', required: true },
            { key: 'code', type: 'text', label: 'Código' },
            { key: 'city', type: 'text', label: 'Cidade' },
            { key: 'state', type: 'text', label: 'UF' },
            { key: 'free_storage_days', type: 'number', label: 'Dias grátis' },
            { key: 'daily_rate_brl', type: 'number', label: 'R$/m³/dia' },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'name', label: 'Nome' },
            { key: 'code', label: 'Código' },
            { key: 'city', label: 'Cidade' },
            { key: 'active', label: 'Ativo', type: 'boolean' },
        ],
    },
    'order-templates': {
        api: 'order-templates',
        label: 'Templates de pedido',
        singular: 'Template',
        fields: [
            { key: 'name', type: 'text', label: 'Nome', required: true },
            { key: 'description', type: 'text', label: 'Descrição' },
            { key: 'active', type: 'boolean', label: 'Ativo' },
        ],
        columns: [
            { key: 'name', label: 'Nome' },
            { key: 'active', label: 'Ativo', type: 'boolean' },
        ],
    },
};

export function ResourceListRoute() {
    const { resourceKey } = useParams<{ resourceKey: string }>();
    const config = resourceKey ? RESOURCE_MAP[resourceKey] : null;
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    useEffect(() => {
        setPage(1);
    }, [search, perPage]);

    const { data, isLoading } = useResourceList<Record<string, unknown>>(config?.api ?? '', {
        search,
        page,
        per_page: perPage,
    });

    if (!config) {
        return <p className="text-sm text-red-600">Recurso não configurado.</p>;
    }

    const rows = data?.data ?? [];
    const total = data?.total ?? rows.length;
    const pagination = data
        ? {
            current_page: data.current_page ?? page,
            last_page: data.last_page ?? 1,
            per_page: data.per_page ?? perPage,
            total,
            from: data.from ?? null,
            to: data.to ?? null,
        }
        : undefined;

    const columns: Column<Record<string, unknown>>[] = config.columns.map((c) => ({
        key: c.key,
        label: c.label,
        align: c.type === 'boolean' ? 'center' : 'left',
        render: (row) => {
            const value = row[c.key];
            if (c.type === 'color') {
                return <ColorSwatch hex={value as string | null | undefined} />;
            }
            if (c.type === 'logo') {
                const logoUrl = row.logo_url as string | null | undefined;
                const thumbColor = (row.thumbnail_color as string | null | undefined) ?? '#9CA3AF';
                const name = String(row.name ?? '');
                if (logoUrl) {
                    return (
                        <img
                            src={logoUrl}
                            alt={name}
                            className="size-10 rounded-lg border border-gray-100 bg-white object-contain p-0.5"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = SUPPLIER_FALLBACK;
                            }}
                        />
                    );
                }
                return (
                    <span
                        className="flex size-10 items-center justify-center rounded-lg text-[10px] font-bold text-white"
                        style={{ backgroundColor: thumbColor }}
                    >
                        {name.slice(0, 2).toUpperCase() || '?'}
                    </span>
                );
            }
            if (c.type === 'boolean') {
                return value ? (
                    <PiCheckCircleBold className="inline size-5 text-emerald-500" />
                ) : (
                    <PiXCircleBold className="inline size-5 text-gray-300" />
                );
            }
            if (value === null || value === undefined || value === '') {
                return <span className="text-gray-300">—</span>;
            }
            // First column highlighted bold; secondary cols as pills
            if (c.key === config.columns[0].key) {
                return <span className="font-semibold text-gray-900">{String(value)}</span>;
            }
            return <BadgePill tone="secondary">{String(value)}</BadgePill>;
        },
    }));

    return (
        <RichListShell<Record<string, unknown>>
            title={config.label}
            breadcrumb={[
                { name: 'Configurações' },
                { name: config.label },
            ]}
            headerActions={
                <Link to={`/settings/${resourceKey}/new`}>
                    <Button color="primary" className="gap-2">
                        <PiPlusBold className="size-4" />
                        Novo {config.singular}
                    </Button>
                </Link>
            }
            search={search}
            onSearch={setSearch}
            searchPlaceholder={`Buscar ${config.label.toLowerCase()}…`}
            isLoading={isLoading}
            columns={columns}
            rows={rows}
            rowKey={(r) => String(r.id)}
            pagination={pagination}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            actionsRender={(row) => (
                <Link
                    to={`/settings/${resourceKey}/${row.id}/edit`}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                    <PiPencilSimpleBold className="size-3.5" />
                    Editar
                </Link>
            )}
            emptyMessage={`Nenhum ${config.singular.toLowerCase()} encontrado.`}
        />
    );
}

export function ResourceFormRoute() {
    const { resourceKey, id } = useParams<{ resourceKey: string; id: string }>();
    const config = resourceKey ? RESOURCE_MAP[resourceKey] : null;
    const navigate = useNavigate();
    const isNew = id === 'new';
    const { data: record, isLoading } = useResourceShow<Record<string, unknown>>(config?.api ?? '', isNew ? undefined : id);
    const { create, update } = useResourceMutation(config?.api ?? '');
    const { register, handleSubmit, reset, watch } = useForm<Record<string, unknown>>();

    useEffect(() => {
        if (record && !isLoading) {
            reset(record);
        }
    }, [record, isLoading, reset]);

    if (!config) {
        return <p className="text-sm text-red-600">Recurso não configurado.</p>;
    }

    if (!isNew && isLoading) {
        return <LoadingState label="Carregando…" />;
    }

    const onSubmit = handleSubmit(async (values) => {
        if (isNew) {
            await create.mutateAsync(values);
        } else if (id) {
            await update.mutateAsync({ id, payload: values });
        }
        navigate(`/settings/${resourceKey}`);
    });

    return (
        <div className="mx-auto max-w-2xl space-y-6">
            <div className="flex items-center gap-3">
                <Link
                    to={`/settings/${resourceKey}`}
                    className="text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                >
                    ← {config.label}
                </Link>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {isNew ? `Novo ${config.singular}` : `Editar ${config.singular}`}
            </h1>
            <form
                onSubmit={onSubmit}
                className="space-y-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900"
            >
                {config.fields.map((field) => (
                    <div key={field.key}>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            {field.label}
                            {field.required && <span className="ml-0.5 text-red-500">*</span>}
                        </label>
                        {field.type === 'boolean' ? (
                            <input
                                type="checkbox"
                                {...register(field.key)}
                                className="h-4 w-4 rounded border-gray-300 text-primary"
                            />
                        ) : field.type === 'color' ? (
                            <ColorPickerField fieldKey={field.key} register={register} watch={watch} />
                        ) : (
                            <input
                                type={field.type === 'number' ? 'number' : 'text'}
                                {...register(field.key)}
                                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none transition focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                            />
                        )}
                    </div>
                ))}
                <div className="flex gap-3 pt-2">
                    <button type="submit" className="btn-gold rounded-xl px-6 py-2.5 text-sm font-semibold">
                        Salvar
                    </button>
                    <Link
                        to={`/settings/${resourceKey}`}
                        className="rounded-xl border border-gray-200 px-6 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                        Cancelar
                    </Link>
                </div>
            </form>
        </div>
    );
}
